class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.upsellRefreshTimeout = null;
    this.upsellRefreshInFlight = false;
    this.addEventListener('click', this.onUpsellAddClick.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  onUpsellAddClick(event) {
    const upsellContainer = event.target?.closest?.('.cart-rebuy-product');
    if (!upsellContainer) return;

    const clicked = event.target?.closest?.('button, a, input');
    if (!clicked) return;

    const isDecline = clicked.classList?.contains('decline');
    const isRebuyAdd = clicked.classList?.contains('rebuy-button') && !isDecline;

    const isThemeAdd =
      clicked.classList?.contains('quick-add__submit') ||
      clicked.matches?.('button[type="submit"]') ||
      clicked.getAttribute?.('name') === 'add' ||
      clicked.getAttribute?.('data-action') === 'add-to-cart';

    if (isRebuyAdd || isThemeAdd) this.queueUpsellRefresh();
  }

  queueUpsellRefresh() {
    if (this.upsellRefreshTimeout) clearTimeout(this.upsellRefreshTimeout);


    this.upsellRefreshTimeout = setTimeout(() => this.refreshFromServer(), 600);
  }

  async refreshFromServer() {
    if (this.upsellRefreshInFlight) return;
    this.upsellRefreshInFlight = true;

    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement && targetElement.outerHTML !== sourceElement.outerHTML) {
          targetElement.replaceWith(sourceElement);
        }
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const sourceCartDrawer = html.querySelector('cart-drawer');
      if (cartDrawer && sourceCartDrawer) {
        cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
      }

      await this.refreshCartIconBubble();
      this.reinitializeUpsellWidgets();
    } catch (e) {
      console.error(e);
    } finally {
      this.upsellRefreshInFlight = false;
    }
  }

  reinitializeUpsellWidgets() {
    
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
    } catch (e) {

    }

    const possibleInitializers = [
      window?.Rebuy?.init,
      window?.Rebuy?.SmartCart?.init,
      window?.Rebuy?.widgets?.init,
      window?.rebuy?.init,
      window?.rebuy?.SmartCart?.init,
    ];

    for (const init of possibleInitializers) {
      if (typeof init !== 'function') continue;
      try {
        init();
        break;
      } catch (e) {
      }
    }
  }

  async refreshCartIconBubble() {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'cart-icon-bubble');

    const response = await fetch(url.toString());
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceSection = html.querySelector('.shopify-section');
    if (sourceSection) cartIconBubble.innerHTML = sourceSection.innerHTML;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
